import {
  Button,
  Box,
  Card as MuiCard,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider as MuiDivider,
  FormControl as MuiFormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField as MuiTextField,
  Typography,
  Checkbox
} from "@mui/material";
import Paper from "@mui/material/Paper";
//Material UI Components and Functions
import { makeStyles } from "@mui/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import MuiAlert from "@mui/material/Alert";
import { spacing } from "@mui/system";
// Redux Components
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

import { connect, useDispatch } from "react-redux";
import styled from "styled-components";
// Custom UI componets and functions:
import { formatMoney } from "../../../setup/helpers";
import { getAccountBalance, hydrateAccount, createWinterCrisisHistory } from "../../account/api/actions";
import { getAllGlCodes } from "../../apl/api/actions";
import validator from 'validator';

import {
  createNewAccountPayment,
  getUserPayments,
  updateNewAccountPaymentErrors,
  updateNewAccountPaymentInProgress,
  createNewPaymentWithExternalServices
} from "../../payments/api/actions";
import { setSnackbar } from "../../snackbar/api/snackbar";
import moment from "moment";
import { getByPropertyId as getPropertyUtilityByPropertyId } from "../../propertyUtility/api/actions";


const CenteredContent = styled.div`
  text-align: center;
`;

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const FormControl = styled(MuiFormControl)(spacing);
const TextField = styled(MuiTextField)(spacing);

const defaultCardFee = 3.25;
const defaultProcessingFee = 1.00;

const PM_STOP_PAYMENTS_MESSAGE =
  "This property is setup for payments to be made directly to your property manager. Please login to your Property’s rent/payment portal or contact your property manager to make your payment.";

function isPmStopPaymentsBlockingAccount(accountData) {
  const account = accountData?.account;
  const managingPm = accountData?.propertyManager;
  if (!account || !managingPm?.stopPayments) {
    return false;
  }
  return account.customerType === "Tenant";
}

// Custom Styles for property selection drop down

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  selectedPropertyLocationLabel: {
    fontSize: 50,
    backgroundColor: "#fffffff",
    borderColor: "#fffffff",
  },
  loadingIcon: {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

function getPaymentActorFields(details) {
  if (!details) {
    return { userID: undefined, firstName: "N/A", lastName: "N/A" };
  }
  const userID = details.id ?? details.userId ?? details.uuid;
  const firstName = details.firstName ?? details.first_name ?? "N/A";
  const lastName = details.lastName ?? details.last_name ?? "N/A";
  return { userID, firstName, lastName };
}

function basePaymentFromUserDetails(details) {
  const { userID, firstName, lastName } = getPaymentActorFields(details);
  return getBasePaymentObject(userID, firstName, lastName);
}

function getBasePaymentObject(userID, firstName = "N/A", lastName = "N/A") {
  const today = moment();
  var baseObj = {
    paymentType: -1,
    paymentTypeDesc: null,
    paymentNote: `INTERNAL APPLICATION PAYMENT FROM USER: ${firstName} (User ID: ${userID})`,
    paymentAmount: 0,

    routingNumber: "",
    accountNumber: "",

    firstName: "",
    lastName: "",
    nameOnCard: "",// also name on account
    ccNum: "",
    ccTypeId: "",
    ccExpMonth: "",
    ccExpYear: "20",
    cardCvv: "",
    paymentDate: today,
    transactionFee: 0,
    transactionTotal: 0,
    checkNumber: "",
    accountsCovered: [],
    billingAddress1: "",
    billingAddress2: "",
    billingCity: "",
    billingState: "",
    billingZip: "",
    glCode: ""
  }
  return baseObj;
}
const PaymentForm = (props) => {
  // Handles vendor drop down
  const [paymentType, setPaymentType] = React.useState("");
  const [glCode, setGlCode] = React.useState("");
  const [paymentTypeDescription, setPaymentTypeDescription] = React.useState("");
  const [formType, setFormType] = React.useState("CASH");
  const [echeckWarning, setEcheckWarning] = React.useState(null);
  const [ccWarning, setCcWarning] = React.useState(null);
  const [confirmWarning, setConfirmWarning] = React.useState(null);
  const [isAutoPayForm, setIsAutopayForm] = React.useState(props.isAutoPayForm || false);
  const [hasAutoPay, setHasAutoPay] = React.useState(false);
  const [useAccountBillingAddress, setUseAccountBillingAddress] = React.useState(false);
  const [showBillingAddressToggle, setShowBillingAddressToggle] = React.useState(false);
  const [newPaymentFormObject, setNewPaymentFormObject] = React.useState(() =>
    basePaymentFromUserDetails(props.user?.details)
  );

  const [propUtilityIsLoaded, setPropUtilityIsLoaded] = React.useState(false);
  const [transactionTotal, setTransactionTotal] = React.useState(0);
  const [feeTotal, setFeeTotal] = React.useState(0);
  const [isOpen, setIsOpen] = React.useState(props.isOpen || false);
  const [disableForm, setDisableForm] = React.useState(true);

  const dispatch = useDispatch();
  
  // Get minimum winter crisis cover amount from settings
  const getMinimumWinterCrisisCoverAmount = () => {
    if (props.setting?.settings?.items && props.setting.settings.items.length > 0) {
      const winterCrisisSetting = props.setting.settings.items.find(setting => setting.name === 'winterCrisisCoverAmount');
      if (winterCrisisSetting && winterCrisisSetting.value) {
        // Ensure proper decimal handling - parse as float and format to 2 decimal places
        const amount = parseFloat(winterCrisisSetting.value);
        return Number(amount.toFixed(2));
      }
    }
    // Fallback to hardcoded value if setting not found
    return 211.00;
  };

  // Check if winter crisis payment meets minimum requirement
  const isWinterCrisisPaymentValid = () => {
    if (props.initialPaymentAmount) {
      const minimumCoverAmount = getMinimumWinterCrisisCoverAmount();
      // Use the form value if it's been set, otherwise use the initial prefilled amount
      // This handles the case where the form hasn't been updated yet by useEffect
      const currentAmount = newPaymentFormObject.paymentAmount > 0 
        ? newPaymentFormObject.paymentAmount 
        : props.initialPaymentAmount;
      return currentAmount >= minimumCoverAmount;
    }
    return true; // Not a winter crisis payment
  };
  
  const getPaymentTypeInfoFromSelectedId = (id) => {
    for (var x = 0; x < props.apl.genericTypes.paymentMethodType.data.length; x++) {
      if (props.apl.genericTypes.paymentMethodType.data[x].id == id) {
        return props.apl.genericTypes.paymentMethodType.data[x];
      }
    }
    return null;
  };
  const updateFormFeeAccordingly = (type) => {
    // Get the current payment amount (don't modify it)
    // Use the form object value, or fall back to initial payment amount if form hasn't been updated yet
    const currentPaymentAmount = newPaymentFormObject.paymentAmount > 0 
      ? newPaymentFormObject.paymentAmount 
      : (props.initialPaymentAmount || 0);

    // Set the new fee based on payment type
    let newFeeTotal = 0;
    if (props.propertyUtilities.data.propertyUtility != null && props.propertyUtilities.data.propertyUtility.id) {
      if (type == "BANK") {
        newFeeTotal = props.propertyUtilities.data.propertyUtility.processingFee;
        setFeeTotal(newFeeTotal);
      }
      if (type == "CARD") {
        newFeeTotal = props.propertyUtilities.data.propertyUtility.creditCardFee;
        setFeeTotal(newFeeTotal);
      }
      if (type == "CASH") {
        setFeeTotal(0);
        newFeeTotal = 0;
      }
    }
    else {
      if (type == "BANK") {
        newFeeTotal = defaultProcessingFee;
        setFeeTotal(newFeeTotal);
      }
      if (type == "CARD") {
        newFeeTotal = defaultCardFee;
        setFeeTotal(newFeeTotal);
      }
      if (type == "CASH") {
        setFeeTotal(0);
        newFeeTotal = 0;
      }
    }
    // Calculate transaction total as current payment amount + new fee
    setTransactionTotal(currentPaymentAmount + newFeeTotal);
  }


  const applyPaymentTypeSelection = (selectedType) => {
    newPaymentFormObject.paymentType = selectedType.id;
    setFormType(selectedType.type);
    setPaymentType(selectedType.id);
    setPaymentTypeDescription(selectedType.description);
    if (selectedType.description != 'Account Credit') {
      setDisableForm(false);
    } else {
      setDisableForm(true);
    }
    if (selectedType.type === "CARD") {
      const types = props.apl.genericTypes.creditCardType?.data || [];
      if (types.length > 0) {
        newPaymentFormObject.ccTypeId = types[0].id;
      }
    }
    updateFormFeeAccordingly(selectedType.type);
  };

  const clearPaymentTypeSelection = () => {
    setPaymentType("");
    setPaymentTypeDescription("");
    setFormType("CASH");
    setDisableForm(true);
    setFeeTotal(0);
    setTransactionTotal(newPaymentFormObject.paymentAmount || 0);
  };

  const pmStopPaymentsBlocked = isPmStopPaymentsBlockingAccount(props.account?.data);

  const handleSelectedPaymentTypeChange = (event) => {
    var selectedType = getPaymentTypeInfoFromSelectedId(event.target.value);
    if (selectedType == null) return;

    if (selectedType.description === "eCheck" && echeckWarning) {
      let message;
      if (echeckWarning === "both") {
        message = "Customer has open Disconnect on the account and multiple bad checks. Do you want to proceed?";
      } else if (echeckWarning === "disconnect") {
        message = "Customer has open Disconnect on the account. Do you want to proceed?";
      } else {
        message = "This customer has multiple bad checks on the account. Are you sure you want to proceed using this payment method?";
      }
      setConfirmWarning({
        message,
        onConfirm: () => applyPaymentTypeSelection(selectedType),
      });
      return;
    }

    if (selectedType.description === "Credit/Debit Card" && ccWarning) {
      setConfirmWarning({
        message: "This customer has multiple bad cards on the account. Are you sure you want to proceed using this payment method?",
        onConfirm: () => applyPaymentTypeSelection(selectedType),
      });
      return;
    }

    applyPaymentTypeSelection(selectedType);
  };

  const handleSelectedGlCodeTypeChange = (event) => {
    setGlCode(event.target.value);
    setDisableForm(false);
  };


  const EvaluateFormObject = () => {
    var newErrors = [];
    // Amount warnings
    if (
      newPaymentFormObject.paymentType == "Credit/Debit Card" &&
      newPaymentFormObject.paymentAmount < 5
    ) {
      newErrors.push(
        "Credit/Debit cards have a minimum transaction value of $5"
      );
    } else if (newPaymentFormObject.paymentAmount <= 0) {
      newErrors.push("Payment Amount must be a positive value.");
    }
    
    // Winter crisis validation - check if payment meets minimum cover amount
    if (props.initialPaymentAmount) {
      const minimumCoverAmount = getMinimumWinterCrisisCoverAmount();
      // Use the form value if it's been set, otherwise use the initial prefilled amount
      const currentAmount = newPaymentFormObject.paymentAmount > 0 
        ? newPaymentFormObject.paymentAmount 
        : props.initialPaymentAmount;
      if (currentAmount < minimumCoverAmount) {
        newErrors.push(`Winter crisis payment must be at least $${minimumCoverAmount.toFixed(2)}`);
      }
    }

    // on error checking update the store and tell
    if (newErrors.length > 0) {
      props.updateNewAccountPaymentErrors(newErrors);
      return false;
    }
    if (
      formType == "CARD" &&
      (newPaymentFormObject.ccTypeId === "" ||
        newPaymentFormObject.ccTypeId === null ||
        newPaymentFormObject.ccTypeId === undefined)
    ) {
      newErrors.push("Select a card type.");
      props.updateNewAccountPaymentErrors(newErrors);
      return false;
    }
    props.updateNewAccountPaymentErrors(newErrors);
    return true;
  };

  useEffect(() => {
    if (!props.account.isLoading && props.account.data.account && props.account.data.account.id && !props.accountDisconnects.isLoading) {
      const hasBadChecks = props.account.data.account.badChecks >= 3;
      let hasOpenDisconnect = false;
      if (props.accountDisconnects.data.accountDisconnectsList.items && props.accountDisconnects.data.accountDisconnectsList.items.length > 0) {
        hasOpenDisconnect = props.accountDisconnects.data.accountDisconnectsList.items.some(
          (d) => d.completed == 0
        );
      }

      if (hasBadChecks && hasOpenDisconnect) {
        setEcheckWarning("both");
      } else if (hasOpenDisconnect) {
        setEcheckWarning("disconnect");
      } else if (hasBadChecks) {
        setEcheckWarning("badChecks");
      } else {
        setEcheckWarning(null);
      }

      if (props.account.data.account.badCards >= 3) {
        setCcWarning("badCards");
      } else {
        setCcWarning(null);
      }
      if(props.account.data.account.autoPayments && props.payments.accountAutoPayments?.items.length > 0) {
        setHasAutoPay(true);
      }
      if (props.account.data.addresses.length == 0) {
        setUseAccountBillingAddress(false);
        setShowBillingAddressToggle(false);
      }
      else {
        setShowBillingAddressToggle(true);
        setUseAccountBillingAddress(true);
      }
      if (props.account.data.property && props.account.data.property.id && !propUtilityIsLoaded && props.propertyUtilities.data.propertyUtility == null) {
        setPropUtilityIsLoaded(true);
        props.getPropertyUtilityByPropertyId(props.account.data.property.id);
      }
    }
    if (props.user.details != null) {
      const baseObject = basePaymentFromUserDetails(props.user.details);
      if (props.initialPaymentAmount) {
        baseObject.paymentAmount = props.initialPaymentAmount;
      }
      setNewPaymentFormObject(baseObject);
    }
    // resets utility lookup loading state upon closing 
    if (!isOpen && propUtilityIsLoaded && props.propertyUtilities.data.propertyUtility != null) {
      setPropUtilityIsLoaded(false);
    }
    if (props.propertyUtilities.data.propertyUtility != null && props.propertyUtilities.data.propertyUtility.id) {
      updateFormFeeAccordingly("CASH");
    }
    if (props.apl.genericTypes.billingAccountGLCode.data == [] && !props.apl.genericTypes.billingAccountGLCode.isLoading) {
      props.getAllGlCodes();
    }
  }, [props.account.isLoading, props.account.data, props.accountDisconnects.isLoading, props.user.details, props.propertyUtilities.data]);

  // Set initial payment amount only when winter crisis payment form opens
    React.useEffect(() => {
    if (props.initialPaymentAmount) {
      const initialAmount = props.initialPaymentAmount;
      const minimumCoverAmount = getMinimumWinterCrisisCoverAmount();
      const updatedPaymentObj = props.user?.details
        ? basePaymentFromUserDetails(props.user.details)
        : { ...newPaymentFormObject };
      updatedPaymentObj.paymentAmount = initialAmount;
      setNewPaymentFormObject(updatedPaymentObj);
      // Calculate transaction total including fees (feeTotal is 0 initially, but will be updated when payment type is selected)
      setTransactionTotal(initialAmount + feeTotal);
      // Clear any existing errors if the initial amount is valid
      if (initialAmount >= minimumCoverAmount) {
        props.updateNewAccountPaymentErrors([]);
      }
    }
  }, [props.initialPaymentAmount, props.setting?.settings?.items, props.user?.details]);

  const classes = useStyles();

  const findPaymentTypeId = (type) => {
    for (var x = 0; x < props.apl.genericTypes.paymentMethodType.data.length; x++) {
      if (props.apl.genericTypes.paymentMethodType.data[x].description == type) {
        return props.apl.genericTypes.paymentMethodType.data[x].id;
      }
    }
    // defaults -1
    return -1;
  }

  const submitPaymentForm = async () => {

    dispatch(setSnackbar(true, "warning", "Submitting Payment"));
    const isProperForm = EvaluateFormObject();
    //if (isProperForm) {
    // If no errors are found
    let paymentObjectToSend = newPaymentFormObject;
    if (formType == "BANK") {
      paymentObjectToSend.nameOnCard = paymentObjectToSend.firstName + " " + paymentObjectToSend.lastName;
    }
    var selectedType = getPaymentTypeInfoFromSelectedId(paymentType);
    paymentObjectToSend.transactionFee = parseFloat(feeTotal.toFixed(2));
    paymentObjectToSend.transactionTotal = paymentObjectToSend.paymentAmount + paymentObjectToSend.transactionFee;
    paymentObjectToSend.transactionTotal = parseFloat(paymentObjectToSend.transactionTotal.toFixed(2));
    paymentObjectToSend.paymentType = paymentType;
    paymentObjectToSend.glCode = glCode;
    paymentObjectToSend.paymentTypeDesc = selectedType.description;
    paymentObjectToSend.accountsCovered = [props.account.data.account];

    if (formType == "CARD") {
      paymentObjectToSend.ccTypeId = Number(newPaymentFormObject.ccTypeId);
    }

    if (useAccountBillingAddress) {
      paymentObjectToSend.billingAddress1 = props.account.data.addresses[0].address1;
      paymentObjectToSend.billingAddress2 = props.account.data.addresses[0].address2;
      paymentObjectToSend.billingCity = props.account.data.addresses[0].city;
      paymentObjectToSend.billingState = props.account.data.addresses[0].state;
      paymentObjectToSend.billingZip = props.account.data.addresses[0].zip;
    }
    let response = await props.createNewPaymentWithExternalServices(paymentObjectToSend);
      if(response.status == 200) {
        // If this is a winter crisis payment, create winter crisis history
        if (props.initialPaymentAmount) {
          try {
            await props.createWinterCrisisHistory(
              paymentObjectToSend.paymentAmount.toFixed(2),
              props.account.data.account.accountNum
            );
            
            // Check if the winter crisis history creation was successful
            if (!props.account.error || props.account.error == '') {
              dispatch(setSnackbar(true, "success", "Winter crisis payment successful"));
            } else {
              dispatch(setSnackbar(true, "error", "Payment successful but failed to create winter crisis history"));
            }
           
          } catch (error) {
            console.error("Winter crisis history creation error:", error);
            const errorMessage = error?.response?.data?.message || error?.message || "Unknown error";
            dispatch(setSnackbar(true, "error", `Payment successful but failed to create winter crisis history: ${errorMessage}`));
          }
        }
        // Pass the response and payment amount to onUpdate so it can update the balance
        if (props.onUpdate) {
          props.onUpdate(response, paymentObjectToSend.paymentAmount);
        }
      }
  };

  const cancelPaymentForm = () => {
    if (props.onCancel) {
      props.onCancel();
    }
  };


  const handleBillingAddressToggle = (event) => {
    setUseAccountBillingAddress(!useAccountBillingAddress);
  };

  // Updates the local state object of "bill in progress" upon each modification
  const onFieldChange =  (event)  =>  {

    if(event.target.name == "feeTotal") {
      setFeeTotal(Number(event.target.value));
      newPaymentFormObject.transactionFee = Number(event.target.value);
      // newPaymentFormObject.transactionTotal = newPaymentFormObject.paymentAmount + newPaymentFormObject.transactionFee;
      // setTransactionTotal(newPaymentFormObject.transactionTotal);
      // newPaymentFormObject.paymentAmount = feeTotal + newPaymentFormObject.paymentAmount;
      setTransactionTotal(parseFloat(newPaymentFormObject.transactionFee.toFixed(2)) + parseFloat(newPaymentFormObject.paymentAmount.toFixed(2)));


    }

    if (event.target.name == "paymentAmount") {
      newPaymentFormObject.paymentAmount = Number(event.target.value);
      setTransactionTotal(newPaymentFormObject.paymentAmount + feeTotal);
    }
    else {
      // Gets the current vendor info data and updates it with the event's name and value:
      if (event.target.type == "number") {
        newPaymentFormObject[event.target.name] = String(event.target.value);
      } else {
        newPaymentFormObject[event.target.name] = event.target.value;
      }
    }
  };

  if (props.account.isLoading) {
    return (
      <Card mb={6}>
        <CardContent>
          <Typography variant="h3" gutterBottom>
            Account Loading...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card mb={6} elevation={3}>
      <CardContent>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, width: '100%' }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
            <FormControl variant="outlined" fullWidth my={2}>
              <InputLabel id="paymentTypeDropDownLabel">
                Payment Type
              </InputLabel>
              <Select
                labelId="paymentTypeDropDownLabel"
                id="paymentTypeDropDown"
                value={paymentType}
                onChange={handleSelectedPaymentTypeChange}
                label="Payment Type"
                name="paymentType"
                fullWidth
                my={2}
              >
                {props.apl.genericTypes.paymentMethodType.data.length > 0 ? (
                  props.apl.genericTypes.paymentMethodType.data.map(
                    (item, key) => {
                      if ((!pmStopPaymentsBlocked && item.description == "Credit/Debit Card") ||
                        item.description == "Account Credit" ||
                        item.description == "Check" ||
                        item.description == "Posting Error" ||
                        item.description == "WB Lockbox" ||
                        item.description == "Customer Refund" ||
                        item.description == "Write Off" ||
                        item.description == "Balance Transfer" ||
                        (!pmStopPaymentsBlocked && hasAutoPay && item.description == "Auto-Pay account") ||
                        (!pmStopPaymentsBlocked && item.description == "eCheck") ||
                        (!isAutoPayForm && item.description == "Cash")) {
                        return (
                          <MenuItem value={item.id} id={key}>
                            {item.description}
                          </MenuItem>
                        );
                      }
                    }
                  )
                ) : (
                  <MenuItem value={-1}>No Payment Method Types Found</MenuItem>
                )}
                ]
              </Select>
            </FormControl>
          </Box>
          {formType == "BANK" && ( // Only show for ach type
            <>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
                <TextField
                  fullWidth
                  type="number"
                  id="routingNumber"
                  variant="outlined"
                  label="Routing Number"
                  inputProps={{ maxLength: 9 }}
                  defaultValue={newPaymentFormObject.routingNumber}
                  onChange={onFieldChange}
                  name="routingNumber"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
                <TextField
                  fullWidth
                  type="text"
                  id="firstName"
                  variant="outlined"
                  label="Account Holder First Name"
                  defaultValue={newPaymentFormObject.firstName}
                  onChange={onFieldChange}
                  name="firstName"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
                <TextField
                  fullWidth
                  type="text"
                  id="lastName"
                  variant="outlined"
                  label="Account Holder Last Name"
                  defaultValue={newPaymentFormObject.lastName}
                  onChange={onFieldChange}
                  name="lastName"
                />
              </Box>
            </>
          )}
          {formType == "CARD" && ( // Only show for credit card type
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
                <FormControl variant="outlined" fullWidth my={2}>
              <InputLabel id="cardTypeDropDownLabel">
                Card Type
              </InputLabel>
              <Select
                fullWidth
                id="cardType"
                variant="outlined"
                label="Card Type"
                labelId="cardTypeDropDownLabel"
                defaultValue={newPaymentFormObject.ccTypeId === "" ? "" : newPaymentFormObject.ccTypeId}
                onChange={(e) => {
                  newPaymentFormObject.ccTypeId = Number(e.target.value);
                }}
                name="ccTypeId"
              >
                {(props.apl.genericTypes.creditCardType?.data || []).length > 0 ? (
                  props.apl.genericTypes.creditCardType.data.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.description}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">No card types loaded</MenuItem>
                )}
            </Select>
            </FormControl>
            </Box>
          )}
          {formType == "BANK" && ( // Only show for ach type
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
              <TextField
                fullWidth
                id="accountNumber"
                variant="outlined"
                label="Account Number"
                type="number"
                inputProps={{ maxLength: 17 }}

                defaultValue={newPaymentFormObject.accountNumber}
                onChange={onFieldChange}
                name="accountNumber"
              />
            </Box>
          )}
          {formType == "CARD" && ( // Only show for credit card type
            <>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
                <TextField
                  my={2}
                  fullWidth
                  type="text"
                  id="nameOnCard"
                  variant="outlined"
                  label="Name On Card"
                  defaultValue={newPaymentFormObject.nameOnCard}
                  onChange={onFieldChange}
                  name="nameOnCard"
                />
              </Box>
            </>
          )}
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
            <TextField
              id="paymentAmount"
              label="Payment Amount (USD)"
              type="number"
              fullWidth
              my={2}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                  inputProps: { 
                      max: 9999, min: 1 
                  }
              }}
              defaultValue={props.initialPaymentAmount || newPaymentFormObject.paymentAmount}
              onChange={onFieldChange}
              name="paymentAmount"
            />
          </Box>
          {/*<Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
              <TextField
                id="glCode"
                label="Gl Code"
                type="number"
                fullWidth
                my={2}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                defaultValue={newPaymentFormObject.glCode}
                onChange={onFieldChange}
                name="glCode"
              />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, width: '100%' }}>
              */}
          {(paymentTypeDescription == "Account Credit" || 
           paymentTypeDescription == "Posting Error"
          || paymentTypeDescription == "Customer Refund" ) && ( // Only show for credit card type //props.apl.genericTypes.billingAccountGLCode.data

            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
              <FormControl variant="outlined" fullWidth my={2}>
                <InputLabel id="glCodeTypeDownLabel">
                  GL Code
                </InputLabel>
                <Select
                  labelId="glCodeTypeDownLabel"
                  id="glCodeTypeDropDown"
                  value={glCode}
                  onChange={handleSelectedGlCodeTypeChange}
                  label="GL Code"
                  name="glCode"
                  fullWidth
                  my={2}
                >
                  {props.apl.genericTypes.billingAccountGLCode.data.length > 0 ? (
                    props.apl.genericTypes.billingAccountGLCode.data.map(
                      (item, key) => {
                        {
                          if (paymentTypeDescription == "Customer Refund" || paymentTypeDescription == "Posting Error") {
                            if (item.accountDescription == "ACH / Web Payment Fee" || item.accountDescription == "Credit Card / Web Payment Fee") {
                              return (
                                <MenuItem value={item.id} id={key}>
                                  {item.accountDescription}
                                </MenuItem>
                              );
                            }
                            
                          }else if (paymentTypeDescription == "Account Credit") {
                            if (item.accountDescription == "NSF/Bank Fee Income"
                            || item.accountDescription == "APL Electric Receipts" 
                            || item.accountDescription == "Late Fee / Penalty Income" 
                            || item.accountDescription == "Disconnect / Reconnect Income"
                            || item.accountDescription == "Maint/Repairs Electric"
                            || item.accountDescription == "OUPS Income"
                            || item.accountDescription == "Lease Lighting Income"
                            || item.accountDescription == "Account Setup Fee") {
                              return (
                                <MenuItem value={item.id} id={key}>
                                  {item.accountDescription}
                                </MenuItem>
                              );
                            }
                          }
                          else{
                            return (
                              <MenuItem value={item.id} id={key}>
                                {item.accountDescription}
                              </MenuItem>
                            );
                          }
                          
                        }
                      }
                    )
                  ) : (
                    <MenuItem value={-1}>No GL Codes Found</MenuItem>
                  )}
                  ]
                </Select>
              </FormControl>
            </Box>
          ) }

          {formType == "CARD" && ( // Only show for credit card type
            <>

              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
                <TextField
                  my={2}
                  fullWidth
                  variant="outlined"
                  type="number"
                  id="ccNum"
                  label="Card Number"
                  inputProps={{ maxLength: 19 }}
                  defaultValue={newPaymentFormObject.ccNum}
                  onChange={onFieldChange}
                  name="ccNum"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
                <TextField
                  my={2}
                  fullWidth
                  type="text"
                  id="ccExpMonth"
                  label="Exp Month (MM)"
                  onChange={onFieldChange}
                  variant="outlined"
                  defaultValue={newPaymentFormObject.ccExpMonth}
                  name="ccExpMonth"
                  inputProps={{ maxLength: 2 }}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
                <TextField
                  my={2}
                  fullWidth
                  type="text"
                  id="ccExpYear"
                  label="Exp Year (YYYY)"
                  onChange={onFieldChange}
                  variant="outlined"
                  inputProps={{ maxLength: 4 }}
                  defaultValue={newPaymentFormObject.ccExpYear}
                  name="ccExpYear"
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(33.33% - 16px)' } }}>
                <TextField
                  my={2}
                  fullWidth
                  type="text"
                  id="cardCvv"
                  variant="outlined"
                  label="CVV"
                  defaultValue={newPaymentFormObject.cardCvv}
                  onChange={onFieldChange}
                  name="cardCvv"
                  inputProps={{ maxLength: 4 }}
                />
              </Box>
            </>
          )}
          {formType == "CHECK" && ( // Only show for credit card type
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(50% - 12px)', lg: '1 1 calc(50% - 12px)' } }}>
              <TextField
                my={2}
                fullWidth
                type="text"
                id="checkNumber"
                label="Check Number"
                value={newPaymentFormObject.checkNumber}
                onChange={onFieldChange}
                autoComplete={"off"}
                //onBlur={() =>{ props.handleBillingProcessSubmission(props.billing.data.billInProgress, false); } }
                variant="outlined"
                name="checkNumber"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          )}
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 100%', md: '1 1 100%', lg: '1 1 calc(66.67% - 16px)' }, mb: 2 }}>
            <TextField
              id="paymentNote"
              label="Payment Note"
              type="text"
              fullWidth
              my={2}
              multiline={true}
              rows={10}
              rowsMax={10}
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              value={newPaymentFormObject.paymentNote}
              onChange={(e) =>
                setNewPaymentFormObject((prev) => ({
                  ...prev,
                  paymentNote: e.target.value,
                }))
              }
              name="paymentNote"
            />
          </Box>
        </Box>
        {!useAccountBillingAddress &&
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: '100%' }}>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(33.33% - 21px)', lg: '1 1 calc(25% - 18px)' } }}>
                <TextField
                  label="Billing Address"
                  type="text"
                  onChange={onFieldChange}
                  name="billingAddress1"
                  defaultValue={newPaymentFormObject.billingAddress1}
                  fullWidth
                  my={2}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(33.33% - 21px)', lg: '1 1 calc(25% - 18px)' } }}>
                <TextField
                  label="Billing Address (Apt)"
                  type="text"
                  onChange={onFieldChange}
                  name="billingAddress2"
                  defaultValue={newPaymentFormObject.billingAddress2}
                  fullWidth
                  my={2}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(33.33% - 21px)', lg: '1 1 calc(25% - 18px)' } }}>
                <TextField
                  label="Billing City"
                  type="text"
                  onChange={onFieldChange}
                  name="billingCity"
                  defaultValue={newPaymentFormObject.billingCity}
                  fullWidth
                  my={2}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(33.33% - 21px)', lg: '1 1 calc(25% - 18px)' } }}>
                <TextField
                  label="Billing State"
                  type="text"
                  onChange={onFieldChange}
                  name="billingState"
                  defaultValue={newPaymentFormObject.billingState}
                  fullWidth
                  my={2}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
              <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(33.33% - 21px)', lg: '1 1 calc(25% - 18px)' } }}>
                <TextField
                  label="Billing Zip"
                  type="text"
                  onChange={onFieldChange}
                  name="billingZip"
                  defaultValue={newPaymentFormObject.billingZip}
                  fullWidth
                  my={2}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            </Box>
          </>
        }
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: '100%' }}>
          <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(75% - 12px)' } }}>
            <TableContainer component={Paper}>
              <Table className={classes.table} aria-label="simple table">
                <TableBody>
                  {(formType == "BANK" || formType == "CARD" || 
                  paymentTypeDescription == "Posting Error" ||
                   paymentTypeDescription == "Customer Refund" ||
                   paymentTypeDescription == "Auto-Pay account" 
                  ) && ( // Only show for credit card type
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <Typography variant="h5">Transaction Fee</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h5">
                          {parseFloat}
                        </Typography>
                        <TextField
                            id="feeTotal"
                            label="Transaction Fee"
                            type="number"
                            onChange={onFieldChange}
                            name="feeTotal"
                            defaultValue={feeTotal}
                            fullWidth
                            my={2}
                            variant="outlined"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <Typography variant="h5">Transaction Total</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h5">
                        {formatMoney(transactionTotal)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            {props.payments.newPaymentSubmissionErrors.map((error) => {
              return <Alert severity="error">{error}</Alert>;
            })}
            {pmStopPaymentsBlocked && (
              <Alert severity="warning">{PM_STOP_PAYMENTS_MESSAGE}</Alert>
            )}

            {props.initialPaymentAmount && !isWinterCrisisPaymentValid() && 
              <Alert severity="error">
                Winter crisis payment must be at least ${getMinimumWinterCrisisCoverAmount().toFixed(2)}
              </Alert>
            }

          </Box>
          {showBillingAddressToggle &&
            <Box sx={{ flex: { xs: '1 1 100%', lg: '1 1 calc(25% - 12px)' } }}>
              <Typography variant="h5">Saved Billing Address</Typography>
              <Checkbox
                checked={useAccountBillingAddress}
                onClick={handleBillingAddressToggle}
              />
            </Box>
          }
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(50% - 16px)', lg: '1 1 calc(50% - 16px)' } }}>
            <Button
              mr={1}
              variant="contained"
              color="secondary"
              fullWidth
              disabled={props.account.data?.account?.stopPayment 
                || (newPaymentFormObject.paymentAmount == 0 && paymentTypeDescription != "Customer Refund" && paymentTypeDescription != "Posting Error" )
                || disableForm
                || (props.initialPaymentAmount && !isWinterCrisisPaymentValid())}
              onClick={submitPaymentForm}
              my={2}
            >Submit new account payment
            </Button>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 calc(50% - 16px)', lg: '1 1 calc(50% - 16px)' } }}>
            <Button
              mr={1}
              variant="contained"
              color="primary"
              fullWidth
              onClick={cancelPaymentForm}
              my={2}>
              Cancel
            </Button>
          </Box>
        </Box>
      </CardContent>

      <Dialog
        open={confirmWarning !== null}
        onClose={() => { setConfirmWarning(null); clearPaymentTypeSelection(); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Payment Warning</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmWarning?.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setConfirmWarning(null); clearPaymentTypeSelection(); }}
            color="inherit"
          >
            No
          </Button>
          <Button
            onClick={() => { confirmWarning?.onConfirm(); setConfirmWarning(null); }}
            variant="contained"
            color="primary"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

// Component Properties
PaymentForm.propTypes = {
  // Store objects:
  user: PropTypes.object.isRequired,
  apl: PropTypes.object.isRequired,
  payments: PropTypes.object.isRequired,
  account: PropTypes.object.isRequired,
  accountDisconnects: PropTypes.object.isRequired,
  propertyUtilities: PropTypes.object.isRequired,

  createNewAccountPayment: PropTypes.func.isRequired,
  getPropertyUtilityByPropertyId: PropTypes.func.isRequired,
  updateNewAccountPaymentInProgress: PropTypes.func.isRequired,
  updateNewAccountPaymentErrors: PropTypes.func.isRequired,
  hydrateAccount: PropTypes.func.isRequired,
  getUserPayments: PropTypes.func.isRequired,
  getAccountBalance: PropTypes.func.isRequired,
  createNewPaymentWithExternalServices: PropTypes.func.isRequired,
  getAllGlCodes: PropTypes.func.isRequired,
};

// Component State
function PaymentFormState(state) {
  return {
    user: state.user,
    apl: state.apl,
    payments: state.payments,
    account: state.account,
    accountDisconnects: state.accountDisconnects,
    propertyUtilities: state.propertyUtilities,
    setting: state.setting
  };
}
export default connect(PaymentFormState, {
  getUserPayments,
  getAccountBalance,
  createNewAccountPayment,
  updateNewAccountPaymentInProgress,
  updateNewAccountPaymentErrors,
  hydrateAccount,
  getPropertyUtilityByPropertyId,
  createNewPaymentWithExternalServices,
  getAllGlCodes,
  createWinterCrisisHistory
})(PaymentForm);
